import React, { forwardRef } from "react";
import { createPortal } from "react-dom";

import l from "components/util/l";
import CommentForm from "components/util/helpers/CommentForm";

const SELECTION_COLOR = "#f4c542";
const POPUP_WIDTH = 280;
const POPUP_MARGIN = 10;
const POPUP_HEIGHT = 340;

const overlayStyle = (isCommenting) => ({
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  cursor: isCommenting ? "default" : "crosshair",
  userSelect: "none",
  backgroundColor: isCommenting ? "rgba(0, 0, 0, 0.35)" : "rgba(0, 0, 0, 0.05)",
});

const boxStyle = (rect, color, isDashed) => ({
  position: "fixed",
  left: rect.left,
  top: rect.top,
  width: rect.width,
  height: rect.height,
  border: `2px ${isDashed ? "dashed" : "solid"} ${color}`,
  backgroundColor: isDashed ? "transparent" : `${color}26`,
  pointerEvents: "none",
});

// The popup hangs below the captured region, pushed back inside the viewport
// when the region sits against an edge. Measured against the window the overlay is
// drawn in, which is not the captured one when the capture runs over an iframe.
const getPopupStyle = (rect, overlayWindow) => ({
  position: "fixed",
  left: Math.max(POPUP_MARGIN, Math.min(rect.left, overlayWindow.innerWidth - POPUP_WIDTH - POPUP_MARGIN)),
  top: Math.max(POPUP_MARGIN, Math.min(rect.top + rect.height + POPUP_MARGIN, overlayWindow.innerHeight - POPUP_HEIGHT)),
  width: POPUP_WIDTH,
  // The overlay blocks selection so the drag never highlights text; the popup
  // has to opt back in or its textarea becomes unselectable.
  userSelect: "text",
  pointerEvents: "auto",
  zIndex: 1,
});

const ScreenshotHelperView = forwardRef(({
  capture,
  captureError,
  comment,
  dragRect,
  isCapturing,
  overlayDocument,
  overlayWindow,
  region,
  onChangeComment,
  onClickCancel,
  onClickRetryCapture,
  onClickSave,
  onMouseDown,
  onMouseMove,
  onMouseUp,
}, ref) => {

  const isCommenting = !!region;

  return createPortal(
    <div
      ref={ref}
      data-screenshot-helper="true"
      style={overlayStyle(isCommenting)}
      onMouseDown={isCommenting ? undefined : onMouseDown}
      onMouseMove={isCommenting ? undefined : onMouseMove}
      onMouseUp={isCommenting ? undefined : onMouseUp}
    >

      {/* HINT BAR */}

      {!isCommenting &&
        <div
          className="d-flex align-items-center px-3 py-2 bg-dark text-white rounded shadow"
          style={{
            position: "fixed",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "0.85rem",
            pointerEvents: "none",
          }}
        >
          <i className="fas fa-crosshairs mr-2" />
          {l("Arrastra para seleccionar un área%ES008")}
          <span className="ml-3 badge badge-secondary">{l("Esc para salir%ES003")}</span>
        </div>}

      {/* REGION DRAG */}

      {dragRect && <div style={boxStyle(dragRect, SELECTION_COLOR, true)} />}

      {/* CONFIRMED SELECTION */}

      {isCommenting && <div style={boxStyle(region.rect, SELECTION_COLOR, false)} />}

      {/* The rasterization freezes the page while it runs, so the wait is announced
          where the user is already looking — over the selection itself — and not only
          inside the comment box. */}

      {isCapturing &&
        <div
          className="d-flex align-items-center px-3 py-2 bg-dark text-white rounded shadow"
          style={{
            position: "fixed",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "0.85rem",
            pointerEvents: "none",
          }}
        >
          <i className="fas fa-spinner fa-spin mr-2" />
          {l("Generando captura...%ES002")}
        </div>}

      {isCommenting &&
        <CommentForm
          isNew={true}
          isSaveDisabled={isCapturing}
          text={comment}
          style={getPopupStyle(region.rect, overlayWindow)}
          onChangeText={onChangeComment}
          onClose={onClickCancel}
          onSave={onClickSave}
        >
          {/* The screenshot lands after the box opens, so the slot keeps its
              height and swaps a spinner for the preview when it arrives. */}
          <div
            className="d-flex align-items-center justify-content-center mb-2 border rounded overflow-hidden"
            style={{ height: 120, backgroundColor: "#f5f5f5" }}
          >
            {capture
              ? <img
                src={capture.dataUrl}
                alt={l("Captura de la selección%ES004")}
                className="w-100 h-100"
                style={{ objectFit: "contain" }}
              />
              : <span className="text-muted small text-center px-2">
                {isCapturing
                  ? <>
                    <i className="fas fa-spinner fa-spin d-block mb-2" />
                    {l("Generando captura...%ES002")}
                  </>
                  : <>
                    <i className="fas fa-triangle-exclamation d-block mb-2" />
                    {l("No se pudo generar la captura%ES005")}
                    {captureError &&
                      <span className="d-block mt-1" style={{ fontSize: "0.7rem", opacity: 0.7 }}>
                        {captureError}
                      </span>}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 mt-1"
                      onClick={onClickRetryCapture}
                    >
                      {l("Reintentar%ES007")}
                    </button>
                  </>}
              </span>}
          </div>
        </CommentForm>}

    </div>,
    overlayDocument.body
  );

});

ScreenshotHelperView.displayName = "ScreenshotHelperView";

export default ScreenshotHelperView;
